"use client";

import { useEffect, useState } from "react";
import { ImageIcon } from "lucide-react";
import {
  BANK_LOGOS,
  THAI_BANKS,
  formatReferenceNo,
  ImageViewer,
  normalizeThaiBankKey,
  StatusBadge,
  ThaiDatetimeText,
} from "@aera/data-table";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { AeraImage } from "@/app/components/ui/AeraImage";
import { formatReceivableExpiryRemaining } from "@/lib/pendingOrders";
import { receivableFieldCellType } from "./receivableDetailsUtils";

function resolveReceivableBankLabel(value: string): string {
  const normalizedKey = normalizeThaiBankKey(value);
  if (!normalizedKey) return value;

  const bank = THAI_BANKS[normalizedKey];
  return bank?.symbol ?? bank?.name ?? bank?.nameLong ?? value;
}

function ReceivableBankCell({ value }: { value: string }) {
  const normalizedKey = normalizeThaiBankKey(value);
  const logoUrl = normalizedKey ? BANK_LOGOS[normalizedKey] : undefined;
  const bankLabel = resolveReceivableBankLabel(value);

  return (
    <div className="grid grid-cols-[20px_auto] items-center gap-2.5">
      <div className="flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-aera-lightgray">
        {logoUrl ? (
          <AeraImage
            src={logoUrl}
            alt={bankLabel}
            width={20}
            height={20}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="text-[8px] font-bold text-aera-lightslate">
            {bankLabel.substring(0, 2)}
          </span>
        )}
      </div>
      <span className="truncate font-semibold text-aera-gray">{bankLabel}</span>
    </div>
  );
}

function ReceivableExpiryCell({
  value,
  variant,
}: {
  value: string;
  variant: "table" | "drawer";
}) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTick((tick) => tick + 1), 30_000);
    return () => window.clearInterval(id);
  }, [value]);

  const label = formatReceivableExpiryRemaining(value, {
    compact: variant === "table",
  });
  const expiresMs = Date.parse(value);
  const isUrgent =
    Number.isFinite(expiresMs) &&
    expiresMs > Date.now() &&
    expiresMs - Date.now() < 3_600_000;
  const isExpired = label === "หมดอายุ";

  if (variant === "drawer") {
    const tone = isExpired || isUrgent
      ? "border-aera-red/20 bg-aera-redlight/40 text-aera-red"
      : "border-aera-amber/25 bg-aera-warmcream/70 text-aera-charcoal";
    return (
      <span
        className={`inline-flex max-w-full items-center rounded-lg border px-2.5 py-1 text-sm font-bold tabular-nums leading-5 ${tone}`}
      >
        {isExpired ? "หมดอายุแล้ว" : label}
      </span>
    );
  }

  const textClass =
    "block whitespace-nowrap font-medium leading-5 text-xs";

  const colorClass = isExpired
    ? "text-aera-red"
    : isUrgent
      ? "text-aera-red"
      : "text-aera-gray";

  return <span className={`${textClass} ${colorClass}`}>{label}</span>;
}

function ReceivableMissingImagePlaceholder() {
  return (
    <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-md bg-aera-lightgray text-aera-lightslate">
      <ImageIcon className="h-3.5 w-3.5" aria-hidden />
    </div>
  );
}

function ReceivableImageCell({
  value,
  variant,
}: {
  value: string;
  variant: "table" | "drawer";
}) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const imageUrl = value.trim();

  useEffect(() => {
    setImageLoadFailed(false);
  }, [imageUrl]);

  if (!imageUrl || imageUrl === "—" || imageLoadFailed) {
    return (
      <div
        className={
          variant === "drawer"
            ? "flex items-center justify-start"
            : "flex items-center justify-start w-full h-full"
        }
      >
        <ReceivableMissingImagePlaceholder />
      </div>
    );
  }

  const thumb = (
    <AeraImage
      src={imageUrl}
      alt="สลิป"
      width={36}
      height={36}
      loading="lazy"
      decoding="async"
      className="h-full w-full object-cover transition-transform group-hover/img:scale-110"
      onError={() => setImageLoadFailed(true)}
    />
  );

  if (variant === "drawer") {
    return (
      <div className="flex items-center justify-start">
        <AeraButton
          variant="ghost"
          size="xs"
          type="button"
          className="group/img h-16 w-16 overflow-hidden rounded-md bg-aera-lightgray p-0!"
          onClick={() => setViewerOpen(true)}
        >
          {thumb}
        </AeraButton>
        <ImageViewer
          open={viewerOpen}
          onOpenChange={setViewerOpen}
          imageUrl={imageUrl}
        />
      </div>
    );
  }

  return (
    <div
      className="flex items-center justify-start w-full h-full"
      onClick={(event) => event.stopPropagation()}
    >
      <AeraButton
        variant="ghost"
        size="xs"
        type="button"
        className="group/img h-9 w-9 overflow-hidden rounded-md bg-aera-lightgray p-0! hover:bg-aera-lightgray/50 active:scale-95"
        onClick={(event) => {
          event.stopPropagation();
          setViewerOpen(true);
        }}
      >
        {thumb}
      </AeraButton>
      <ImageViewer
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        imageUrl={imageUrl}
      />
    </div>
  );
}

export function ReceivableFieldCell({
  fieldKey,
  value,
  variant = "table",
}: {
  fieldKey: string;
  value: string;
  variant?: "table" | "drawer";
}) {
  if (receivableFieldCellType(fieldKey) === "image") {
    return (
      <ReceivableImageCell
        value={!value || value === "—" ? "" : value}
        variant={variant}
      />
    );
  }

  if (!value || value === "—") {
    return <span className="text-aera-lightslate">—</span>;
  }

  if (fieldKey === "status") {
    const parts = value
      .split(/[•|,]/g)
      .map((s) => s.trim())
      .filter(Boolean);
    const statuses = parts.length > 0 ? parts : [value.trim()];
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {statuses.map((status) => (
          <StatusBadge key={status} status={status} />
        ))}
      </div>
    );
  }

  const textClass =
    variant === "drawer"
      ? "block max-w-full whitespace-pre-wrap break-words font-medium leading-6 text-aera-charcoal"
      : "block max-w-full truncate font-medium leading-5 text-aera-gray";

  switch (receivableFieldCellType(fieldKey)) {
    case "amount": {
      const isRemaining =
        fieldKey === "slip_amount" ||
        fieldKey === "amount_remaining" ||
        fieldKey === "amount";
      return (
        <span
          className={`block w-full font-bold leading-5 tabular-nums tracking-tight ${
            isRemaining ? "text-aera-green" : "text-aera-charcoal"
          } ${variant === "table" ? "text-right" : ""}`}
        >
          {value}
        </span>
      );
    }
    case "datetime":
      return <ThaiDatetimeText value={value} variant="table" />;
    case "expiry":
      return <ReceivableExpiryCell value={value} variant={variant} />;
    case "bank":
      return <ReceivableBankCell value={value} />;
    case "reference":
      return (
        <span
          className={
            variant === "drawer"
              ? "block max-w-full whitespace-pre-wrap break-all font-medium leading-6 text-aera-charcoal tabular-nums"
              : "block font-medium leading-5 text-aera-gray tabular-nums whitespace-nowrap"
          }
        >
          {variant === "table" ? formatReferenceNo(value) : value}
        </span>
      );
    case "image":
      return <ReceivableImageCell value={value} variant={variant} />;
    case "tel":
      return (
        <a
          href={`tel:${value}`}
          className={`${textClass} transition-colors hover:text-aera-charcoal hover:underline cursor-pointer`}
          onClick={(event) => event.stopPropagation()}
        >
          {value}
        </a>
      );
    default: {
      return <span className={textClass}>{value}</span>;
    }
  }
}
